import { useEffect, useMemo, useState } from "react";
import { App, Button, Input, Modal, Result, Segmented, Select, Tooltip, theme } from "antd";
import { TbArchive, TbPlus, TbSearch } from "react-icons/tb";
import { LifeCycle, Resource } from "../../dtos/stage.dto";
import { RESOURCE_LABELS, RESOURCE_OPTIONS } from "./constants";
import StageForm, { StageFormValues } from "./form";
import StageList from "./list";
import { ApiService } from "api/api.service";

export interface StageManagerModalProps {
  orgCode: string | undefined;
  onClose: () => void;
  defaultResource?: Resource;
}

type EditorState = { mode: "create" } | { mode: "edit"; id: string } | { mode: "idle" };

const StageManagerModal = ({ orgCode, onClose, defaultResource = Resource.APPLICATION }: StageManagerModalProps) => {
  const { token } = theme.useToken();
  const { message } = App.useApp();

  const stagesQuery = ApiService.organisation.useGetAllStages(orgCode);
  const createStage = ApiService.organisation.useAddStage();
  const updateStage = ApiService.organisation.useUpdateStage();

  const [resource, setResource] = useState<Resource>(defaultResource);
  const [search, setSearch] = useState("");
  const [scope, setScope] = useState<"active" | "archived">("active");
  const [editor, setEditor] = useState<EditorState>({ mode: "idle" });

  const allStages = (stagesQuery.data ?? []).filter((stage) => stage.external !== true);
  const saving = createStage.isPending || updateStage.isPending;

  useEffect(() => {
    setEditor({ mode: "idle" });
    setSearch("");
    setScope("active");
  }, [resource, orgCode]);

  const resourceStages = useMemo(() => allStages.filter((stage) => stage.resource === resource), [allStages, resource]);

  const selected = useMemo(
    () => (editor.mode === "edit" ? (allStages.find((stage) => stage._id === editor.id) ?? null) : null),
    [editor, allStages],
  );

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return resourceStages.filter((stage) => {
      if (scope === "archived" ? !stage.archived : stage.archived) return false;
      if (!needle) return true;
      return stage.label.toLowerCase().includes(needle) || stage.value.toLowerCase().includes(needle);
    });
  }, [resourceStages, search, scope]);

  const takenValues = useMemo(() => resourceStages.map((stage) => stage.value), [resourceStages]);

  const conflictingEntryLabel = useMemo(
    () =>
      resourceStages.find(
        (stage) => stage.lifecycle === LifeCycle.ENTRY && !stage.archived && stage._id !== selected?._id,
      )?.label,
    [resourceStages, selected],
  );

  const archivedCount = useMemo(() => resourceStages.filter((stage) => stage.archived).length, [resourceStages]);

  const handleSubmit = async (values: StageFormValues) => {
    if (!orgCode) return;
    if (editor.mode === "create") {
      const created = await createStage.mutateAsync({
        orgCode,
        payload: { ...values, resource },
      });
      setEditor(created?.result._id ? { mode: "edit", id: created.result._id } : { mode: "idle" });
      stagesQuery.refetch();
      message.success(created.message || "Stage created");
    } else if (editor.mode === "edit") {
      const updated = await updateStage.mutateAsync({
        orgCode,
        id: editor.id,
        payload: {
          label: values.label,
          lifecycle: values.lifecycle,
          icon: values.icon,
          color: values.color,
          public: values.public,
        },
      });
      stagesQuery.refetch();
      message.success(updated.message || "Changes saved");
    }
  };

  const handleArchiveToggle = async (archived: boolean) => {
    if (editor.mode !== "edit" || !orgCode) return;
    await updateStage.mutateAsync({ orgCode, id: editor.id, payload: { archived } });
    setScope(archived ? "archived" : "active");
    message.success(archived ? "Stage archived" : "Stage restored");
  };

  const resourceLabel = RESOURCE_LABELS[resource] ?? resource;

  return (
    <Modal
      open={orgCode !== undefined}
      onCancel={onClose}
      footer={null}
      width={940}
      centered
      destroyOnHidden
      title={
        <div>
          <div style={{ fontSize: 18, fontWeight: 600 }}>Stages</div>
          <div style={{ fontSize: 13, fontWeight: 400, color: token.colorTextTertiary }}>
            The steps a record moves through, per resource.
          </div>
        </div>
      }
      styles={{ body: { padding: 0 } }}
    >
      <div
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
          padding: "12px 24px",
          borderTop: `1px solid ${token.colorBorderSecondary}`,
          borderBottom: `1px solid ${token.colorBorderSecondary}`,
        }}
      >
        <Select value={resource} onChange={setResource} options={RESOURCE_OPTIONS} style={{ width: 180 }} />
        <Input
          allowClear
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search by name or key"
          prefix={<TbSearch size={15} color={token.colorTextQuaternary} />}
          style={{ maxWidth: 260 }}
        />
        <Tooltip title={archivedCount === 0 ? "Nothing archived yet" : undefined}>
          <Segmented
            value={scope}
            onChange={(next) => setScope(next as "active" | "archived")}
            options={[
              { value: "active", label: "Active" },
              {
                value: "archived",
                label: (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <TbArchive size={14} />
                    <span style={{ fontVariantNumeric: "tabular-nums" }}>{archivedCount}</span>
                  </span>
                ),
                disabled: archivedCount === 0,
              },
            ]}
          />
        </Tooltip>
        <Button
          type="primary"
          icon={<TbPlus size={16} />}
          style={{ marginLeft: "auto" }}
          disabled={!orgCode || stagesQuery.isError}
          onClick={() => setEditor({ mode: "create" })}
        >
          New stage
        </Button>
      </div>

      {stagesQuery.isError ? (
        <div style={{ height: 520, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Result
            status="warning"
            title="Couldn't load stages"
            subTitle="The request failed. Retry, or close and reopen this window."
            extra={
              <Button type="primary" onClick={() => void stagesQuery.refetch()} loading={stagesQuery.isFetching}>
                Retry
              </Button>
            }
          />
        </div>
      ) : (
        <div style={{ display: "flex", height: 520 }}>
          <div
            style={{
              width: 320,
              flexShrink: 0,
              overflowY: "auto",
              borderRight: `1px solid ${token.colorBorderSecondary}`,
              background: token.colorFillQuaternary,
            }}
          >
            <StageList
              stages={visible}
              loading={stagesQuery.isLoading}
              selectedId={selected?._id ?? null}
              emptyHint={
                search
                  ? "No stage matches that search."
                  : scope === "archived"
                    ? "Nothing archived for this resource."
                    : `No stages for ${resourceLabel.toLowerCase()} yet. Create the first one.`
              }
              onSelect={(stage) => setEditor({ mode: "edit", id: stage._id })}
            />
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            {editor.mode === "idle" || (editor.mode === "edit" && !selected) ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 12,
                  height: "100%",
                  padding: 32,
                  textAlign: "center",
                }}
              >
                <p style={{ margin: 0, maxWidth: 280, color: token.colorTextTertiary, textWrap: "pretty" }}>
                  Pick a stage on the left to edit it, or add a new one for {resourceLabel.toLowerCase()}.
                </p>
                <Button icon={<TbPlus size={16} />} disabled={!orgCode} onClick={() => setEditor({ mode: "create" })}>
                  New stage
                </Button>
              </div>
            ) : (
              <StageForm
                key={editor.mode === "edit" ? editor.id : "create"}
                mode={editor.mode}
                stage={selected}
                resourceLabel={resourceLabel}
                saving={saving}
                takenValues={takenValues}
                conflictingEntryLabel={conflictingEntryLabel}
                onSubmit={handleSubmit}
                onArchive={() => void handleArchiveToggle(true)}
                onRestore={() => void handleArchiveToggle(false)}
                onCancel={() => setEditor({ mode: "idle" })}
              />
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};

export default StageManagerModal;
